import Cookies from "js-cookie";

// Actions
import { fetchCart } from "../cart/actions";
import { fetchGuest, setGuestAnonymousId } from "../guest/actions";
import { fetchActiveSubscriptions } from "../subscription/actions";
import { setInitialOutOfStockState } from "../out-of-stock/actions";

// Seletors
import subscriptionSelectors from "../subscription/selectors";
import userSelectors from "../user/selectors";

// Observers
import applyCodeObserver from "../apply-code/observer";
import pendingCodeObserver from "../pending-code/observer";
import bannersObserver from "../banners/observer";

// Utils
import { analytics } from "../../utils/analytics";
import {
  authAndFetchUser,
  hasLoggedOutUser,
  identifyUser,
} from "../../utils/currentUser";
import launchDarkly, { variation } from "../../utils/launchDarkly";
import { kustomer } from "../../utils/kustomer";
import { lux } from "../../utils/lux";
import { getBundleCartLimit } from "../../utils/bundle";

export function appLoaded() {
  return dispatch => {
    // Initialize any client state.
    dispatch(setInitialOutOfStockState());

    // Start the applyCode and pendingCode observers.
    applyCodeObserver.start();
    pendingCodeObserver.start();
    if (variation("bundle-banner-existing")) {
      bannersObserver.start();
    }

    // Clear the current guest-id cookie if the user has logged out.
    if (hasLoggedOutUser()) {
      Cookies.remove("guest_id", {
        domain: process.env.GATSBY_COOKIE_DOMAIN,
      });
    }

    const fetchGuestPromise = dispatch(fetchGuest());
    const loadSegmentUserPromise = _loadSegmentUser();

    // Wait for the guest and the segment user to load before updating the
    // guest's anonymous id.
    Promise.all([fetchGuestPromise, loadSegmentUserPromise]).then(values => {
      const userInfo = values[1];
      dispatch(setGuestAnonymousId(userInfo.anonymousId));
    });

    // Wait for the guest and the current user (if any) to be authed before
    // marking initial authorization as complete.
    Promise.all([fetchGuestPromise, authAndFetchUser()]).then(() => {
      dispatch(authInitialized());
    });

    dispatch({
      type: "APP_LOADED",
    });
  };
}

export function authInitialized() {
  return dispatch => {
    const fetchDataAfterAuthPromise = dispatch(fetchDataAfterAuth());
    dispatch({
      type: "AUTH_INITIALIZED",
    });
    return fetchDataAfterAuthPromise;
  };
}

export function fetchDataAfterAuth() {
  return dispatch => {
    dispatch({
      type: "FETCH_AFTER_AUTH_START",
    });

    const productLimit = getBundleCartLimit();

    // When the auth is updated, we should re-fetch the cart and subscription
    // data.
    const fetchSubscriptionsPromise =
      productLimit > 1
        ? dispatch(_fetchActiveSubscriptions())
        : Promise.resolve();
    const fetchCartPromse = dispatch(fetchCart()).then(() => {
      // Once segment is loaded, we should identify the user. We wait until
      // after the cart is fetched to ensurre that the cartId is included in
      // the identify call.
      identifyUser();
    });

    return Promise.all([fetchCartPromse, fetchSubscriptionsPromise])
      .then(() => {
        dispatch({
          type: "FETCH_AFTER_AUTH_SUCCESS",
        });
      })
      .catch(error => {
        dispatch({
          type: "FETCH_AFTER_AUTH_FAILURE",
        });
      });
  };
}

function _fetchActiveSubscriptions() {
  return (dispatch, getState) => {
    // If the user is not logged in, don't fetch the subscriptions.
    if (!userSelectors.isLoggedIn(getState())) return Promise.resolve();

    return dispatch(fetchActiveSubscriptions()).then(() => {
      const isExistingSubscriber = subscriptionSelectors.hasActiveSubscription(
        getState(),
      );
      // Push an attribute to Optimizely indicating whether the user is an
      // existing subscriber.
      return launchDarkly.pushAttributes({
        existingSubscriber: isExistingSubscriber,
      });
    });
  };
}

function _loadSegmentUser() {
  return analytics.waitForUserInfo().then(userInfo => {
    // Once the user is loaded, identify the user with LaunchDarkly.
    launchDarkly.identifyFromUserInfo(userInfo);

    lux.setLuxDataFromUser(userInfo);
    kustomer.setKustomerDataFromUser(userInfo);
    return userInfo;
  });
}
